import React from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import { graphql } from '@apollo/react-hoc';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import toString from 'lodash/toString';
import { compose } from 'react-recompose';

import Menu from '../../../../components/Menu';
import Input from '../../../../components/Input';
import Button from '../../../../components/Button';
import Form from '../../../../components/Form';
import Accordion from '../../../../components/Accordion';

import { SimpleLoadingComponent } from '../../../../utils/apollo';
import { isUrlValid } from '../../../../utils/url';

import InlineErrorMessage from '../inlineErrorMessage';
import * as actions from '../../actions';
import * as constants from '../../constants';
import messages from '../../messages';
import '../../styles.css';

const LinksHeader = (props) => (
  <h3 styleName="links-header">
    {props.title}
  </h3>
);

LinksHeader.propTypes = {
  title: PropTypes.string.isRequired,
};

export class LinksDropdownComponent extends React.Component {
  static propTypes = {
    viewer: PropTypes.object,
    setApplyOnlineLink: PropTypes.func.isRequired,
    setRequestInformationLink: PropTypes.func.isRequired,
    setNextLinkOrder: PropTypes.func.isRequired,
    setLearnMoreLinks: PropTypes.func.isRequired,
    editLearnMoreLink: PropTypes.func.isRequired,
    onShowAddLinkModal: PropTypes.func.isRequired,
    removeLearnMoreLink: PropTypes.func.isRequired,
    setAreNewLinks: PropTypes.func.isRequired,
    requestInformationLink: PropTypes.object,
    applyOnlineLink: PropTypes.object,
    learnMoreLinks: PropTypes.array,
    hasRegisteredChanges: PropTypes.bool,
    nextLinkOrder: PropTypes.number,
  };

  state = {
    activeAccordionIndex: -1,
  }

  componentDidMount = () => {
    const { requestInformationLink, applyOnlineLink, learnMoreLinks, hasRegisteredChanges, nextLinkOrder } = this.props;
    const hubsWebLinks = get(this.props, 'viewer.hubsWebLinks.premium_links', []) || [];

    if (hubsWebLinks.length) {
      this.props.setAreNewLinks({
        areNewLinks: false,
      });
    }

    if (nextLinkOrder === 0 && !hasRegisteredChanges) {
      this.props.setNextLinkOrder(Math.max(...hubsWebLinks.map((link) => Number.parseInt(link.order, 10))) + 1);
    }

    const apolloRequestInformationLink = hubsWebLinks && hubsWebLinks.find((link) => link.category === constants.COMMUNICATE);
    const apolloApplyOnlineLink = hubsWebLinks && hubsWebLinks.find((link) => link.category === constants.APPLY_ONLINE);
    const apolloLearnMoreLinks = hubsWebLinks ? hubsWebLinks.filter((link) => link.category === constants.LEARN_MORE) : [];

    if (isEmpty(applyOnlineLink) && apolloApplyOnlineLink) {
      this.props.setApplyOnlineLink({
        applyOnlineLink: {
          topic: apolloApplyOnlineLink.topic,
          category: apolloApplyOnlineLink.category,
          info: apolloApplyOnlineLink.info,
          url: apolloApplyOnlineLink.url,
          order: apolloApplyOnlineLink.order,
          active: apolloApplyOnlineLink.active,
          product_id: apolloApplyOnlineLink.product_id,
        },
        hasRegisteredChanges: false,
      });
    }
    if (isEmpty(requestInformationLink) && apolloRequestInformationLink) {
      this.props.setRequestInformationLink({
        requestInformationLink: {
          topic: apolloRequestInformationLink.topic,
          category: apolloRequestInformationLink.category,
          info: apolloRequestInformationLink.info,
          url: apolloRequestInformationLink.url,
          order: apolloRequestInformationLink.order,
          active: apolloRequestInformationLink.active,
          product_id: apolloRequestInformationLink.product_id,
        },
        hasRegisteredChanges: false,
      });
    }
    if (isEmpty(learnMoreLinks) && !hasRegisteredChanges && apolloLearnMoreLinks) {
      const learnMore = apolloLearnMoreLinks.map((link) => ({
        active: link.active,
        category: link.category,
        info: link.info,
        order: link.order,
        topic: link.topic,
        url: link.url,
        product_id: link.product_id,
      }));
      this.props.setLearnMoreLinks({
        learnMoreLinks: learnMore,
        hasRegisteredChanges: false,
      });
    }
  };

  hasNewChanges = (requestInformationLink, applyOnlineLink, learnMoreLinks) => {
    const premiumLinks = get(this.props, 'viewer.hubsWebLinks.premium_links');
    const initialRequestInfoLink = premiumLinks && premiumLinks.find((link) => link.category === constants.COMMUNICATE);
    const initialApplyOnlineLink = premiumLinks && premiumLinks.find((link) => link.category === constants.APPLY_ONLINE);
    const initialLearnMoreLinks = premiumLinks && premiumLinks.filter((link) => link.category === constants.LEARN_MORE);

    return (
      toString(requestInformationLink) !== get(initialRequestInfoLink, 'url', '') ||
      toString(applyOnlineLink) !== get(initialApplyOnlineLink, 'url', '') ||
      learnMoreLinks.length !== (initialLearnMoreLinks ? initialLearnMoreLinks.length : 0) ||
      !!Object.keys(learnMoreLinks).find((key) => learnMoreLinks[key].url !== initialLearnMoreLinks[key].url)
    );
  }

  onLinksClick = (e, titleProps) => {
    const { index } = titleProps;
    const { activeAccordionIndex } = this.state;
    const newIndex = activeAccordionIndex === index ? -1 : index;

    this.setState({ activeAccordionIndex: newIndex });
  }

  onChangeRequestInfoLink = ({ target: { value } }) => {
    const { applyOnlineLink, learnMoreLinks } = this.props;

    this.props.setRequestInformationLink({
      requestInformationLink: {
        topic: 'Request Information',
        category: constants.COMMUNICATE,
        info: 'Request information link',
        url: value,
        order: 0,
        active: true,
        product_id: get(this.props.requestInformationLink, 'product_id', null),
        isUrlInvalid: !isUrlValid(value),
      },
      hasRegisteredChanges: this.hasNewChanges(value, get(applyOnlineLink, 'url'), learnMoreLinks),
    });
  }

  onChangeApplyOnlineLink = ({ target: { value } }) => {
    const { requestInformationLink, learnMoreLinks } = this.props;
    let applyOnlineLink = {};

    if (value !== '') {
      applyOnlineLink = {
        topic: 'Apply Online',
        category: constants.APPLY_ONLINE,
        info: 'Apply online link',
        url: value,
        order: 1,
        active: true,
        product_id: get(this.props.applyOnlineLink, 'product_id', null),
        isUrlInvalid: !isUrlValid(value),
      };
    }

    this.props.setApplyOnlineLink({
      applyOnlineLink,
      hasRegisteredChanges: this.hasNewChanges(get(requestInformationLink, 'url'), value, learnMoreLinks),
    });
  }

  onChangeLearnMoreLink = (newUrl, link) => {
    const { requestInformationLink, applyOnlineLink, learnMoreLinks } = this.props;
    const updatedLearnMoreLinks = Object.keys(learnMoreLinks).map((key) => (
      (learnMoreLinks[key].product_id === link.product_id) ? { ...learnMoreLinks[key], url: newUrl } : learnMoreLinks[key]));

    this.props.editLearnMoreLink({
      learnMoreLink: {
        topic: link.topic,
        category: link.category,
        info: link.info,
        url: newUrl,
        order: link.order,
        active: link.active,
        product_id: link.product_id,
        isUrlInvalid: !isUrlValid(newUrl),
      },
      hasRegisteredChanges: this.hasNewChanges(get(requestInformationLink, 'url'), get(applyOnlineLink, 'url'), updatedLearnMoreLinks),
    });
  }

  onRemoveLink = (link) => this.props.removeLearnMoreLink(link);

  render() {
    const { requestInformationLink, applyOnlineLink, onShowAddLinkModal, learnMoreLinks } = this.props;
    const { activeAccordionIndex } = this.state;
    const requestInfoUrl = get(requestInformationLink, 'url', '');
    const requestInfoUrlIsInvalid = get(requestInformationLink, 'isUrlInvalid', false);
    const applyOnlineUrl = get(applyOnlineLink, 'url', '');
    const applyOnlineUrlIsInvalid = get(applyOnlineLink, 'isUrlInvalid', false);
    const learnMoreLinksCount = get(learnMoreLinks, 'length');

    return (
      <Accordion as={Menu} fluid styleName="links-profiles-dropdown">
        <Menu.Item as="li" styleName="dropdown-menu-item">
          <Accordion.Title
            id="links-accordion-title"
            content="Links"
            active={activeAccordionIndex === 0}
            index={0}
            onClick={this.onLinksClick}
            styleName="profiles-links-accordion-title"
          />
          <Accordion.Content
            active={activeAccordionIndex === 0}
            content={(
              <div styleName="links-expanded-area">
                <Form>
                  <LinksHeader title="COMMUNICATE" />
                  <div styleName="links-subheader">
                    Request Information
                  </div>
                  <div styleName="links-input">
                    <Input
                      id="request-info-link-input"
                      aria-label="Request Information URL"
                      fluid
                      placeholder="Enter URL"
                      value={requestInfoUrl}
                      onChange={this.onChangeRequestInfoLink}
                    />
                    <InlineErrorMessage
                      message="URL provided is not in a valid format"
                      isVisible={!!requestInfoUrl && requestInfoUrlIsInvalid}
                    />
                  </div>
                  <hr styleName="links-hr" />
                  <LinksHeader title="APPLY ONLINE" />
                  <div styleName="links-input">
                    <Input
                      id="apply-online-link-input"
                      aria-label="Apply Online URL"
                      fluid
                      placeholder="Enter URL"
                      onChange={this.onChangeApplyOnlineLink}
                      value={applyOnlineUrl}
                    />
                    <InlineErrorMessage
                      message="URL provided is not in a valid format"
                      isVisible={!!applyOnlineUrl && applyOnlineUrlIsInvalid}
                    />
                  </div>
                  <hr styleName="links-hr" />
                  <LinksHeader title="LEARN MORE" />
                  <div styleName="links-blue-box" data-cy="Premium-Hem-Learn-More-Links-Count">
                    {learnMoreLinksCount} out of 10 custom links added
                  </div>
                  {learnMoreLinks.sort((a, b) => a.order - b.order).map((link, index) => (
                    <div key={link.order}>
                      <div styleName="links-subheader">
                        <div>
                          {link.topic}
                        </div>
                        <div>
                          <Button id={`remove-link-${index}`} type="button" negative compact onClick={() => this.onRemoveLink(link)}>
                            Remove
                          </Button>
                        </div>
                      </div>
                      <div styleName="links-input">
                        <Input
                          aria-label={link.topic}
                          fluid
                          placeholder="Enter URL"
                          value={link.url || ''}
                          onChange={(e) => { this.onChangeLearnMoreLink(e.target.value, link); }}
                        />
                        <InlineErrorMessage
                          message="URL provided is not in a valid format"
                          isVisible={link.isUrlInvalid}
                        />
                      </div>
                    </div>
                  ))}
                  <Button type="button" onClick={onShowAddLinkModal} disabled={learnMoreLinks.length >= 10} data-cy="Premium-Hem-Add-New-Link-Button">
                    <FormattedMessage {...messages.createNewLink} />
                  </Button>
                </Form>
              </div>
            )}
          />
        </Menu.Item>
      </Accordion>
    );
  }
}

const query = gql`
  query CollegeProfileLinksDropdown {
    viewer {
      id
      heInstitution
      hubsWebLinks {
        premium_links {
          active
          url
          info
          topic
          category
          order
          begin_date
          end_date
          product_id
        }
      }
    }
  }
`;

const mapStateToProps = (state) => ({
  requestInformationLink: get(state, 'nonPersistedCollegeProfile.linksAndProfiles.requestInformationLink'),
  applyOnlineLink: get(state, 'nonPersistedCollegeProfile.linksAndProfiles.applyOnlineLink'),
  learnMoreLinks: get(state, 'nonPersistedCollegeProfile.linksAndProfiles.learnMoreLinks'),
  hasRegisteredChanges: get(state, 'nonPersistedCollegeProfile.linksAndProfiles.hasRegisteredChanges'),
  nextLinkOrder: get(state, 'nonPersistedCollegeProfile.linksAndProfiles.nextOrder'),
});

export default compose(
  connect(mapStateToProps, { ...actions }),
  SimpleLoadingComponent(graphql(query)),
)(LinksDropdownComponent);
